import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, Link as LinkIcon, File } from "lucide-react";
import AddSnippetForm from "./forms/AddSnippetForm";
import AddFileForm from "./forms/AddFileForm";
import AddLinkForm from "./forms/AddLinkForm";
import { KnowledgeItem } from "@/services/fileService";

interface AddKnowledgeItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemType?: string;
  onSuccess?: (item?: KnowledgeItem) => void;
  agentId?: string | null;
  initialLinkValues?: {
    title?: string;
    url?: string;
    description?: string;
  };
  defaultUrl?: string;
  defaultType?: string;
  organizationLevel?: boolean;
}

const AddKnowledgeItemDialog = ({
  open,
  onOpenChange,
  itemType,
  onSuccess,
  agentId,
  initialLinkValues,
}: AddKnowledgeItemDialogProps) => {
  const [activeTab, setActiveTab] = useState(
    initialLinkValues
      ? "links"
      : itemType === "snippets"
        ? "snippets"
        : itemType === "links"
          ? "links"
          : "files",
  );

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleSuccess = (item?: KnowledgeItem) => {
    if (onSuccess) {
      onSuccess(item);
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add to Knowledge Base</DialogTitle>
          <DialogDescription>
            Add a new item to your {agentId ? "agent's" : "organization's"}{" "}
            knowledge base
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="snippets">
              <FileText className="mr-2 h-4 w-4" />
              Snippet
            </TabsTrigger>
            <TabsTrigger value="links">
              <LinkIcon className="mr-2 h-4 w-4" />
              Link
            </TabsTrigger>
            <TabsTrigger value="files">
              <File className="mr-2 h-4 w-4" />
              File
            </TabsTrigger>
          </TabsList>

          <TabsContent value="snippets">
            <AddSnippetForm onSuccess={handleSuccess} agentId={agentId} />
          </TabsContent>

          <TabsContent value="links">
            <AddLinkForm
              onSuccess={handleSuccess}
              agentId={agentId}
              initialValues={initialLinkValues}
            />
          </TabsContent>

          <TabsContent value="files">
            <AddFileForm onSuccess={handleSuccess} agentId={agentId} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AddKnowledgeItemDialog;
